/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * ONE description of every sweep the agents can run. A "sweep" is a sequence of
 * lab experiments where one control variable is stepped and an observable measured
 * at each setting; each one emits a SweepChartAttachment into the chat log.
 *
 * WHY A REGISTRY. Adding a sweep used to mean editing six places — the sweepKind
 * union in types.ts (twice), the kind→tool map and the kind→label map in
 * repeatSweep.ts, the persona read-only allowlist, and a hand-written tool-name
 * comparison in every chat hook's repeat fast-path. Four of those are pure data and
 * the fifth is derivable, so they all live here now: the union is generated from
 * this table, and a chat hook asks "is this sweep in MY catalog?" instead of
 * string-matching a tool name it has to remember to update.
 *
 * Pure data + lookups: no store, no Firebase, no tool imports (the tool CATALOGS
 * import this, not the other way round — importing them here would drag the whole
 * molecule catalog into the docking bundle). Verified by `npm run test:sweepregistry`.
 */

// Which tool catalog owns a sweep. A chat hook can only execute the sweeps in the
// catalog it hands to its agent, so this is what makes the repeat fast-path safe:
// a marker left by a Molecular Modeling project names a tool the crystal chat has
// never heard of, and the mismatch is data rather than a forgotten `if`.
export type SweepCatalog = 'molecule' | 'crystal' | 'docking';

export interface SweepDef {
  // Stable id persisted on the attachment and on projectState.lastSweep. NEVER
  // rename one: an old saved project carries it.
  kind: string;
  tool: string;
  // How the sweep is named back to the user ("Re-running the Boyle experiment").
  label: string;
  // Does the sweep HOLD a single temperature that a "repeat it hotter" can change?
  // False when temperature is the swept axis (then a temperature target defers to
  // the agent, which shifts the whole range instead) or when the sweep has no
  // temperature at all.
  isothermal: boolean;
  catalog: SweepCatalog;
  // May two runs of this sweep be drawn on the same axes? False for a categorical
  // series (bars keyed by name), where a second run means different bars, not a
  // second curve.
  overlayable: boolean;
}

export const SWEEP_DEFS = [
  { kind: 'boyle', tool: 'runBoyleSweep', label: 'Boyle', isothermal: true, catalog: 'molecule', overlayable: true },
  {
    kind: 'charles',
    tool: 'runCharlesSweep',
    label: 'Charles',
    isothermal: false,
    catalog: 'molecule',
    overlayable: true,
  },
  {
    kind: 'gaylussac',
    tool: 'runGayLussacSweep',
    label: 'Gay-Lussac',
    isothermal: false,
    catalog: 'molecule',
    overlayable: true,
  },
  {
    kind: 'avogadro',
    tool: 'runAvogadroSweep',
    label: 'Avogadro',
    isothermal: true,
    catalog: 'molecule',
    overlayable: true,
  },
  {
    kind: 'caloric',
    tool: 'runCaloricSweep',
    label: 'caloric',
    isothermal: false,
    catalog: 'molecule',
    overlayable: true,
  },
  {
    kind: 'melting',
    tool: 'runMeltingSweep',
    label: 'melting',
    isothermal: false,
    catalog: 'molecule',
    overlayable: true,
  },
  {
    // One protocol per run (heat → hold → cool); runs with different cooling
    // rates overlay as energy-vs-time curves. Temperature is the swept axis.
    kind: 'anneal',
    tool: 'runAnnealSchedule',
    label: 'anneal',
    isothermal: false,
    catalog: 'molecule',
    overlayable: true,
  },
  {
    kind: 'polymerscaling',
    tool: 'runPolymerScalingSweep',
    label: 'polymer scaling',
    isothermal: true,
    catalog: 'molecule',
    overlayable: true,
  },
  {
    // One push of a nanocar (or a lone fullerene) across a gold layer: the
    // chart is rolled distance against traveled distance, sampled along the run.
    kind: 'nanocarpush',
    tool: 'runNanocarPush',
    label: 'nanocar push',
    isothermal: true,
    catalog: 'molecule',
    overlayable: true,
  },
  {
    kind: 'crystalmelting',
    tool: 'runCrystalMeltingSweep',
    label: 'crystal melting',
    isothermal: false,
    catalog: 'crystal',
    overlayable: true,
  },
  {
    kind: 'heating',
    tool: 'runHeatingCurve',
    label: 'heating curve',
    isothermal: false,
    catalog: 'crystal',
    overlayable: true,
  },
  {
    // Hot end / cold end with thermostat groups, the middle energy-conserving:
    // the temperature profile along the sample and κ from the baths' bookkeeping.
    kind: 'heatconduction',
    tool: 'runHeatConduction',
    label: 'heat conduction',
    isothermal: false,
    catalog: 'crystal',
    overlayable: true,
  },
  {
    // One held temperature per run (the growth temperature), so "repeat it at
    // 1400 K" is a legitimate repeat; runs at different temperatures overlay
    // as growth-vs-time curves on the same axes.
    kind: 'seededgrowth',
    tool: 'runSeededGrowth',
    label: 'seeded crystal growth',
    isothermal: true,
    catalog: 'crystal',
    overlayable: true,
  },
  {
    // A LADDER of seeded-growth runs that brackets the melting point; its chart
    // already carries one curve per temperature, so a repeat is a new chart.
    kind: 'meltingbracket',
    tool: 'runMeltingPointBracket',
    label: 'melting-point bracket',
    isothermal: false,
    catalog: 'crystal',
    overlayable: false,
  },
  {
    // Molecular lab: a liquid pooled under gravity in the sealed box, held at a
    // ladder of temperatures — vapor pressure vs temperature from the count
    // of molecules above the pool.
    kind: 'evaporation',
    tool: 'runEvaporationSweep',
    label: 'evaporation',
    isothermal: false,
    catalog: 'molecule',
    overlayable: true,
  },
  {
    // Molecular lab: the box's top is opened — the vapor above the pool is
    // pumped away every few picoseconds while no heat bath runs — and the pool's
    // temperature is charted against time: evaporative cooling.
    kind: 'evaporativecooling',
    tool: 'runEvaporativeCooling',
    label: 'evaporative cooling',
    isothermal: false,
    catalog: 'molecule',
    overlayable: true,
  },
  {
    // Crystal lab: solid–vapor (sublimation) or liquid–vapor coexistence in a
    // periodic slab with a vacuum gap — vapor pressure vs temperature.
    kind: 'vapoursweep',
    tool: 'runVapourSweep',
    label: 'vapor pressure',
    isothermal: false,
    catalog: 'crystal',
    overlayable: true,
  },
  {
    // Crystal lab: guests hopping cage to cage through a FIXED molecular-sieve
    // framework — cumulative hops per guest vs time, one curve per species.
    kind: 'sievepermeation',
    tool: 'runSievePermeation',
    label: 'sieve permeation',
    isothermal: true,
    catalog: 'crystal',
    overlayable: false,
  },
  {
    kind: 'crystalanneal',
    tool: 'runCrystalAnneal',
    label: 'crystal anneal',
    isothermal: false,
    catalog: 'crystal',
    overlayable: true,
  },
  {
    kind: 'carriers',
    tool: 'runCarrierSweep',
    label: 'carrier statistics',
    isothermal: false,
    catalog: 'crystal',
    overlayable: true,
  },
  {
    // Nanostructures: HOMO–LUMO gap vs size (quantum-dot diameter, nanotube
    // diameter, ribbon width) from real-space tight binding — no temperature.
    kind: 'confinement',
    tool: 'runConfinementSweep',
    label: 'quantum confinement',
    isothermal: false,
    catalog: 'crystal',
    overlayable: true,
  },
  {
    // Nanoparticle coordination histogram — categorical bars, one chart per particle.
    kind: 'coordination',
    tool: 'runSurfaceAnalysis',
    label: 'coordination analysis',
    isothermal: false,
    catalog: 'crystal',
    overlayable: false,
  },
  {
    // Lindemann index vs temperature on a thermostat ramp, one curve per particle size.
    kind: 'nanomelting',
    tool: 'runNanoparticleMeltingSweep',
    label: 'nanoparticle melting',
    isothermal: false,
    catalog: 'crystal',
    overlayable: true,
  },
  {
    // Free energy of a gas crossing a graphene nanopore, one curve per gas.
    kind: 'membranepermeation',
    tool: 'runMembranePermeation',
    label: 'membrane permeation',
    isothermal: true,
    catalog: 'crystal',
    overlayable: false,
  },
  {
    // Molecules that reached the lower chamber of a membrane cell vs time, one
    // curve per gas — a live MD run, the counterpart of runMembranePermeation.
    kind: 'membranecrossing',
    tool: 'runMembraneCrossing',
    label: 'membrane crossing',
    isothermal: true,
    catalog: 'crystal',
    overlayable: false,
  },
  {
    // Stress vs strain of a stretched tube / ribbon / wire.
    kind: 'tensile',
    tool: 'runTensileTest',
    label: 'tensile test',
    isothermal: false,
    catalog: 'crystal',
    overlayable: true,
  },
  {
    // Band gap vs axial strain of infinite nanotubes, one curve per (n,m).
    kind: 'straingap',
    tool: 'runNanotubeStrainSweep',
    label: 'nanotube strain response',
    isothermal: false,
    catalog: 'crystal',
    overlayable: false,
  },
  {
    // Gay-Berne rods: S against a temperature ramp (the clearing point).
    kind: 'nematic',
    tool: 'runNematicOrderSweep',
    label: 'nematic order',
    isothermal: false,
    catalog: 'molecule',
    overlayable: true,
  },
  {
    // Gay-Berne rods: how many rod centers sit at each height along the director
    // (one peak per layer in a smectic, flat in a nematic), at the lab's temperature.
    kind: 'rodlayers',
    tool: 'runRodLayerProfile',
    label: 'layer profile',
    isothermal: true,
    catalog: 'molecule',
    overlayable: true,
  },
  {
    // Gay-Berne rods: S against time while the aligning field is switched on and off.
    kind: 'rodfield',
    tool: 'runRodFieldSwitch',
    label: 'field switching',
    isothermal: true,
    catalog: 'molecule',
    overlayable: true,
  },
  // Coarse-grained observables against a temperature ramp (or, for clusters,
  // against time): the soft-matter examples' investigations.
  // A reactive lab's dissociation equilibrium against a temperature ladder, up and back down.
  {
    kind: 'equilibrium',
    tool: 'runReactionEquilibriumSweep',
    label: 'reaction equilibrium',
    isothermal: false,
    catalog: 'molecule',
    overlayable: true,
  },
  {
    kind: 'diffusion',
    tool: 'runDiffusionSweep',
    label: 'self-diffusion',
    isothermal: false,
    catalog: 'molecule',
    overlayable: true,
  },
  {
    kind: 'gyration',
    tool: 'runGyrationSweep',
    label: 'radius of gyration',
    isothermal: false,
    catalog: 'molecule',
    overlayable: true,
  },
  {
    kind: 'clusters',
    tool: 'runClusterSweep',
    label: 'lipid clusters',
    isothermal: false,
    catalog: 'molecule',
    overlayable: true,
  },
  {
    // One chain against its own reference frame: superposed RMSD and the
    // fraction of native contacts, against time (does the fold hold?) or
    // against a temperature ramp (where does it unfold?).
    kind: 'stability',
    tool: 'runFoldStability',
    label: 'fold stability',
    isothermal: false,
    catalog: 'molecule',
    // Each run is one chain against its own reference: a new chart, never drawn
    // over another protein's curve or squeezed onto a longer run's time axis.
    overlayable: false,
  },
  {
    // A periodic cell under the barostat: density and cell volume against a
    // temperature ramp (thermal expansion, and the volume jump at boiling).
    kind: 'density',
    tool: 'runDensitySweep',
    label: 'density at constant pressure',
    isothermal: false,
    catalog: 'molecule',
    overlayable: true,
  },
  {
    kind: 'dockingresidues',
    tool: 'runResidueContributionChart',
    label: 'residue contributions',
    isothermal: false,
    catalog: 'docking',
    overlayable: false,
  },
  {
    kind: 'dockingposes',
    tool: 'runDockingPoseChart',
    label: 'docking poses',
    isothermal: false,
    catalog: 'docking',
    overlayable: false,
  },
  {
    kind: 'ligandseries',
    tool: 'runLigandSeriesSweep',
    label: 'ligand series',
    isothermal: false,
    catalog: 'docking',
    overlayable: false,
  },
] as const satisfies readonly SweepDef[];

// The sweepKind union, generated from the table — so a new entry above is
// immediately legal on SweepChartAttachment and projectState.lastSweep, and a kind
// that isn't in the table is a type error rather than a chart nobody can repeat.
export type SweepKind = (typeof SWEEP_DEFS)[number]['kind'];
export type SweepTool = (typeof SWEEP_DEFS)[number]['tool'];

const BY_KIND = new Map<string, SweepDef>(SWEEP_DEFS.map((d) => [d.kind, d]));
const BY_TOOL = new Map<string, SweepDef>(SWEEP_DEFS.map((d) => [d.tool, d]));

export const sweepByKind = (kind: string): SweepDef | undefined => BY_KIND.get(kind);
export const sweepByTool = (tool: string): SweepDef | undefined => BY_TOOL.get(tool);

/** The tool that re-runs a recorded sweep kind. */
export const toolForSweepKind = (kind: string): SweepTool | undefined => BY_KIND.get(kind)?.tool as SweepTool;

/** How to name the sweep in prose ("Re-running the {label} experiment"). */
export const labelForSweepKind = (kind: string): string => BY_KIND.get(kind)?.label ?? kind;

/** Temperature is a HELD condition (so a repeat can change it), not the swept axis. */
export const isIsothermalSweep = (kind: string): boolean => BY_KIND.get(kind)?.isothermal ?? false;

/** Two runs of this sweep can share one set of axes. */
export const isOverlayableSweep = (kind: string): boolean => BY_KIND.get(kind)?.overlayable ?? false;

/** Every sweep tool a given chat can actually execute. */
export const sweepToolsInCatalog = (catalog: SweepCatalog): string[] =>
  SWEEP_DEFS.filter((d) => d.catalog === catalog).map((d) => d.tool);

/** Can this chat run that tool? The guard every repeat fast-path uses. */
export const sweepToolInCatalog = (tool: string, catalog: SweepCatalog): boolean =>
  BY_TOOL.get(tool)?.catalog === catalog;
